import type { FC } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import ListItemCheckbox from '@wix/da-ds/pkg/List/ListItem/dsPresets/ListItemCheckbox';
import ListItemDiv from '@wix/da-ds/pkg/List/ListItem/dsPresets/ListItemDiv';
import DeviationThumb from '../../../StudioDeviationList/_partials/DeviationThumb';
import DeviationInfo from '../../../StudioDeviationList/_partials/DeviationInfo';
import DeviationTitle from '../../../StudioDeviationList/_partials/DeviationTitle';
import DeviationActions from '../../../tableActions/DeviationActions';
import OriginalStatsMobile from '../../../products/OriginalStatsMobile';
import { OriginalsData } from '../../../../types/store';

import s from './OriginalListItem.scss';

export interface Props {
  deviation: PapiDeviation;
  originalsData?: OriginalsData;
  offset: number;
  isSelectable?: boolean;
  isUnlisted?: boolean;
  isSelected?: boolean;
  isInSelectMode?: boolean;
  onSelect?: any;
}

const OriginalListItem: FC<Props> = ({
  deviation,
  originalsData,
  offset,
  isSelectable,
  isUnlisted,
  isSelected,
  isInSelectMode,
  onSelect,
}) => {
  const { t } = useTranslation();
  const showStats = !isUnlisted;

  const originals = deviation.originals;
  const basePrice = originals?.basePrice;
  const dollarPrice = basePrice ? formatDollars(basePrice) : '---';
  const isClosed = (originals as any)?.isOpen === false;

  const thumbWithOverlay = (
    <div className={s['thumb-wrapper']}>
      <DeviationThumb deviation={deviation} width={100} height={64} />
      {isClosed && !isUnlisted && (
        <div className={s['closed-overlay']}>
          <span className={s['closed-text']}>
            {t('studio.products.original.closedForNewOrders')}
          </span>
        </div>
      )}
    </div>
  );

  const commonProps = {
    prefix: thumbWithOverlay,
    label: <DeviationTitle deviation={deviation} showSaleIndicators={false} />,
    subLabel: (
      <div className={s['sublabel']}>
        <DeviationInfo deviation={deviation} />
        <div className={s['starting-price']}>
          {t('studio.products.original.startingPrice', { price: dollarPrice })}
        </div>
      </div>
    ),
    suffix: isInSelectMode ? null : (
      <DeviationActions
        deviation={deviation}
        offset={offset}
        contextMenuProps={{ wrapperClassName: s['context-menu'] }}
        onlyContextMenu
      />
    ),
  };

  const listItem = !isSelectable ? (
    <ListItemDiv className={s['list-item']} {...commonProps} />
  ) : (
    <ListItemCheckbox
      {...commonProps}
      className={cn(s['list-item'], s['selectable'])}
      checked={isSelected}
      controlAlignment="center"
      onChange={checked => onSelect(offset, checked)}
    />
  );

  return (
    <div className={s['root']}>
      {listItem}
      {showStats && (
        <div className={s['stats']}>
          <OriginalStatsMobile
            deviation={deviation}
            originalsData={originalsData}
          />
        </div>
      )}
    </div>
  );
};
OriginalListItem.displayName = 'OriginalListItem';

export default OriginalListItem;
